/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useEffect, useState } from 'react';

import { ministriesService, type Ministry } from '../../services/ministries.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import ScreenHeader from '../../components/common/screen-header';

const MinistryView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'ministries',
    basePath: '/ministries',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.MINISTRIES);
  const canDelete = useCanDelete(JOB_NAMES.MINISTRIES);

  const [data, setData] = useState<Ministry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useBreadcrumbTitle(data?.name);

  useEffect(() => {
    if (!isReady || !id) {
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await ministriesService.getById(id);
        setData(res);
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch ministry'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  const handleEdit = () => {
    if (id) navigateToEdit(id);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await ministriesService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete ministry'));
    }
  };

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !data) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Ministry not found'} className="mb-4 w-full" />
        <Button
          label="Back to Ministries"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={() => setDeleteDialogOpen(false)} />
      <Button label="Delete" severity="danger" onClick={handleDeleteConfirm} />
    </div>
  );

  return (
    <div className="p-3" data-testid="SCR-Ministry-View">
      <ScreenHeader icon="pi pi-building" title="Ministry Details" onBack={() => navigateToList()} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <i className="pi pi-building text-blue-600 dark:text-blue-400" style={{ fontSize: '1.25rem' }} />
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-xl font-bold text-ink">{data.name}</h2>
              {(data as any).isDelete || data.isDeleted ? (
                <Tag value="Deleted" severity="danger" className="text-xs px-1.5 py-0.5" />
              ) : (
                <Tag value="Active" severity="success" className="text-xs px-1.5 py-0.5" />
              )}
            </div>
            <p className="text-xs text-ink-muted">
              Ministry Configuration
            </p>
          </div>
        </div>

        {!((data as any).isDelete || data.isDeleted) && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button label="Edit" icon="pi pi-pencil" onClick={handleEdit} size="small" />
            )}
            {canDelete && (
              <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={handleDeleteClick} size="small" />
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Ministry Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Name</label>
              <p className="text-sm text-ink">{data.name || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-muted">Logo Path</label>
              <p className="text-sm text-ink mt-1 break-all">{data.logoPath || '-'}</p>
            </div>
          </div>
        </div>

        <AuditInfoCard
          createdAt={data.createdAt}
          updatedAt={data.updatedAt}
          createdBy={data.createdBy}
          updatedBy={data.updatedBy}
        />
      </div>

      <Dialog
        visible={deleteDialogOpen}
        onHide={() => setDeleteDialogOpen(false)}
        header="Delete Ministry"
        footer={deleteDialogFooter}
        style={{ width: '400px' }}
        modal
        dismissableMask
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <i className="pi pi-exclamation-triangle text-red-600" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{data.name}</strong>?
            </p>
            <p className="text-xs text-ink-muted mt-2">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default MinistryView;
